import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import PrimaryButton from "../components/PrimaryButton";
import { MobileResponsive } from "./MobileResponsive";
import { headersData } from "../data/tableData";
import TernaryButton from "../components/TernaryButton";
import { CustomInput } from "../components/CustomInput";
import useDataFetch from "../hooks/useDataFetch";
import OutletOrderHistoryTable from "../components/OutletOrderHistoryTable";
import { useDispatch } from "react-redux";
import {
  cancelTripService,
  deleteOutletService,
  enableOutletService,
  suspendOutletService,
  updateCancelTripData,
  updateOutletData,
} from "../features/outletSlice";
import toast from "react-hot-toast";
import {
  changeMetrics,
  fetchMetrics,
} from "../features/pricingMetricsSlice";
import { convertToNumber } from "../utils/convertToNum";
import { otpService } from "../features/authSlice";
import FloatingInput from "../components/FloatingInput";
import { Map } from "@vis.gl/react-google-maps";
import { MapMarker } from "../components/MapMarker";
import { InfoWindowPopup } from "../components/InfoWindow";
import { getDefaultCenter, getEntityData } from "../utils/mapUtils";
import DynamicMetaTags from "../components/DynamicMetaTags";
import Avatar from "react-avatar";
import { fetchOrdersApi } from "../api/services/orderService";
import outletService from "../api/services/outletService";

const OutletDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { data, isLoading, mutate } = useDataFetch(
    `/admin/outlet-detail/${id}`,
    updateOutletData
  );

  const [metricsData, setMetricsData] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);

  const { data: user, isLoading: isLoading2 } = useDataFetch(`/admin/profile`);

  useEffect(() => {
    fetchOrdersApi({ outletId: id, page: 1 }).then((res) => {
      setRecentOrders(res.orders || []);
    }).catch(() => setRecentOrders([]));
  }, [id]);

  // Fetch metrics data for the specific outlet
  useEffect(() => {
    const loadMetricsData = async () => {
      if (id) {
        try {
          const resultAction = await dispatch(fetchMetrics(id));
          if (fetchMetrics.fulfilled.match(resultAction)) {
            setMetricsData(resultAction.payload);
          }
        } catch (error) {
          console.error("Error loading metrics data:", error);
        }
      }
    };

    loadMetricsData();
  }, [dispatch, id]);

  const [sidebars, setSidebars] = useState({
    orderPricing: false,
    suspendOutlet: false,
    deleteOutlet: false,
    driversPoint: false,
    cancelTrip: false,
    urbanPiper: false,
  });

  const [urbanPiperKey, setUrbanPiperKey] = useState("");
  const [urbanPiperSaving, setUrbanPiperSaving] = useState(false);
  const [urbanPiperSetupLoading, setUrbanPiperSetupLoading] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);

  const [pricing, setPricing] = useState({
    priceFor1: { value: "", disabled: true },
    priceFor2: { value: "", disabled: true },
    priceFor3: { value: "", disabled: true },
  });

  const [validationErrors, setValidationErrors] = useState({
    priceFor1: false,
    priceFor2: false,
    priceFor3: false,
  });

  const [errors, setErrors] = useState({
    otp: "",
  });

  const orderPricingRef = useRef(null);
  const suspendOutletRef = useRef(null);
  const deleteOutletRef = useRef(null);
  const driversPointRef = useRef(null);
  const cancelTripRef = useRef(null);
  const urbanPiperRef = useRef(null);

  const sidebarRefs = useMemo(() => ({
    orderPricing: orderPricingRef,
    suspendOutlet: suspendOutletRef,
    deleteOutlet: deleteOutletRef,
    driversPoint: driversPointRef,
    cancelTrip: cancelTripRef,
    urbanPiper: urbanPiperRef,
  }), []);

  const [suspendOutlet, setSuspendOutlet] = useState({
    suspendOutlet: (!isLoading && data?.outlet?.is_suspended) || false,
    suspendOutletConfirm: (!isLoading && data?.outlet?.is_suspended) || false,
    enableSuspendedOutlet: (!isLoading && data?.outlet?.is_suspended) || false,
  });

  const [deleteOutlet, setDeleteOutlet] = useState({
    deleteOutlet: false,
  });
  const [otp, setOtp] = useState(false);

  const [cancelTripData, setCancelTripData] = useState({});
  const [cancelTrip, setCancelTrip] = useState(false);

  /* Google Maps */
  const [selectedMarker, setSelectedMarker] = useState({
    selectedMarker: null,
    entityData: [],
  });
  const [infoWindowShown, setInfoWindowShown] = useState(false);

  const handleMarkerClick = (id, marker, entityType) => {
    setSelectedMarker(() => ({
      selectedMarker: marker,
      entityData: getEntityData(id, entityType, data?.outlet),
    }));
    setInfoWindowShown(true);
  };

  const handleCloseInfoWindow = () => {
    setInfoWindowShown(false);
    setSelectedMarker({
      selectedMarker: null,
      entityData: null,
    });
  };

  const onMapClick = useCallback(() => {
    setSelectedMarker({
      selectedMarker: null,
      entityData: [],
    });
    setInfoWindowShown(false);
  }, []);

  useEffect(() => {
    if (
      selectedMarker.entityData?.length > 0 &&
      selectedMarker.selectedMarker
    ) {
      setInfoWindowShown(true);
    }
  }, [selectedMarker]);

  useEffect(() => {
    if (metricsData) {
      setPricing({
        priceFor1: { value: metricsData?.first_order_price, disabled: true },
        priceFor2: {
          value: metricsData?.second_order_price,
          disabled: true,
        },
        priceFor3: {
          value: metricsData?.third_order_price,
          disabled: true,
        },
      });
    }
  }, [metricsData]);

  const toggleSidebar = (key, value) => {
    setSidebars((prev) => ({ ...prev, [key]: value }));
  };

  const handleCloseSidebar = () => {
    setSidebars({
      orderPricing: false,
      suspendOutlet: false,
      deleteOutlet: false,
      driversPoint: false,
      cancelTrip: false,
      urbanPiper: false,
    });
  };

  const handleSaveUrbanPiperKey = async (e) => {
    e.preventDefault();
    if (!urbanPiperKey.trim()) {
      toast.error("Please enter an API key.");
      return;
    }
    setUrbanPiperSaving(true);
    try {
      const res = await outletService.saveUrbanPiperKey({ outletId: id, apiKey: urbanPiperKey.trim() });
      toast.success(res?.data?.message || "Urbanpiper key saved.");
      setUrbanPiperKey("");
      handleCloseSidebar();
      mutate();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save key.");
    } finally {
      setUrbanPiperSaving(false);
    }
  };

  const handleSetupUrbanPiper = async () => {
    setUrbanPiperSetupLoading(true);
    try {
      const res = await outletService.setupUrbanPiper({ outletId: id });
      toast.success(res?.data?.message || "Urbanpiper setup initiated.");
      setShowSetupModal(true);
      mutate();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to initiate setup.");
    } finally {
      setUrbanPiperSetupLoading(false);
    }
  };

  const handlePriceChange = (e, key) => {
    let inputValue = e.target.value.replace(/[^0-9.]/g, ""); // Keep only numbers and decimal
    if (inputValue.split(".").length > 2) return;

    setPricing((prevState) => ({
      ...prevState,
      [key]: { ...prevState[key], value: inputValue },
    }));

    if (inputValue) {
      setValidationErrors((prevErrors) => ({ ...prevErrors, [key]: false }));
    }
  };

  const togglePriceEdit = (key) => {
    setPricing((prevState) => {
      const isDisabled = prevState[key].disabled;
      const currentValue = prevState[key].value;

      if (!isDisabled && !currentValue) {
        setValidationErrors((prevErrors) => ({ ...prevErrors, [key]: true }));
      } else if (!isDisabled) {
        setValidationErrors((prevErrors) => ({ ...prevErrors, [key]: false }));
      }

      return {
        ...prevState,
        [key]: {
          value: isDisabled
            ? currentValue
            : String(currentValue).replace(/^£/, ""),
          disabled: !isDisabled,
        },
      };
    });
  };

  const handleSuspendOutlet = () => {
    setSuspendOutlet((prev) => ({
      ...prev,
      suspendOutlet: true,
    }));
  };

  const handleSuspendOutletConfirm = async () => {
    const resultAction = await dispatch(suspendOutletService({ outletID: id }));

    if (suspendOutletService.fulfilled.match(resultAction)) {
      toast.success(resultAction?.payload?.message);
      setSuspendOutlet((prev) => ({
        ...prev,
        suspendOutletConfirm: true,
      }));
      mutate();
    } else {
      alert(resultAction?.payload);
    }
  };

  const handleEnableSuspendedOutlet = async () => {
    const resultAction = await dispatch(enableOutletService({ outletID: id }));

    if (enableOutletService.fulfilled.match(resultAction)) {
      toast.success(resultAction?.payload?.message);
      setSuspendOutlet({
        suspendOutlet: false,
        suspendOutletConfirm: false,
        enableSuspendedOutlet: false,
      });

      setSidebars({
        orderPricing: false,
        suspendOutlet: false,
        deleteOutlet: false,
        driversPoint: false,
        cancelTrip: false,
      });

      mutate();
    } else {
      alert(resultAction?.payload);
    }
  };

  const handleOtpBlur = useCallback(() => {
    if (!otp || otp.length !== 5) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        otp: "OTP should be 5 digits long",
      }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, otp: "" }));
    }
  }, [otp]);

  const handleSendOtp = async () => {
    if (!isLoading2) {
      const resultAction = await dispatch(otpService({ email: user?.email }));

      if (otpService.fulfilled.match(resultAction)) {
        toast.success(resultAction?.payload?.message);

        setDeleteOutlet((prevState) => ({
          ...prevState,
          deleteOutlet: true,
        }));
      }
    }
  };

  const handleDeleteOutlet = async () => {
    const resultAction = await dispatch(
      deleteOutletService({ outletID: id, otp: otp })
    );

    if (deleteOutletService.fulfilled.match(resultAction)) {
      toast.success(resultAction?.payload?.message);
      navigate("/outlets");
    }
  };

  const handleOrderPricing = async (e) => {
    e.preventDefault();

    // Check if all inputs are saved (disabled)
    let isAllInputsSaved = Object.values(pricing).every(
      (item) => item.disabled
    );

    // Check for validation errors (empty values)
    let errors = {
      priceFor1: !pricing.priceFor1.value,
      priceFor2: !pricing.priceFor2.value,
      priceFor3: !pricing.priceFor3.value,
    };

    setValidationErrors(errors);

    // If any input is still in edit mode or there are validation errors, do not submit
    if (!isAllInputsSaved || Object.values(errors).some((error) => error)) {
      toast.error("Please save all inputs and make sure none are empty.");
      return;
    }

    // If all inputs are saved and valid, proceed with form submission logic
    const resultAction = await dispatch(
      changeMetrics({
        outlet_id: id,
        firstOrderPrice: convertToNumber(pricing.priceFor1.value),
        secondOrderPrice: convertToNumber(pricing.priceFor2.value),
        thirdOrderPrice: convertToNumber(pricing.priceFor3.value),
      })
    );

    if (changeMetrics.fulfilled.match(resultAction)) {
      toast.success(resultAction?.payload?.message);
    }
  };

  const handleCancelTrip = (data) => {
    setCancelTripData(data);
    dispatch(updateCancelTripData(data));
    toggleSidebar("cancelTrip", true);
  };

  const handleCancelTripConfirm = async () => {
    const resultAction = await dispatch(
      cancelTripService({
        orderId: cancelTripData?.orderId,
        driverId: cancelTripData?.driver?.id,
      })
    );

    if (cancelTripService.fulfilled.match(resultAction)) {
      toast.success(resultAction?.payload?.message);
      mutate();
      setCancelTrip(true);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !Object.keys(sidebarRefs).some((key) =>
          sidebarRefs[key]?.current?.contains(event.target)
        )
      ) {
        handleCloseSidebar();
      }
    };

    if (Object.values(sidebars).some((val) => val)) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sidebars, sidebarRefs]);

  const renderTable = () => (
    <div className="flex flex-col gap-5 w-full">
      <OutletOrderHistoryTable
        headers={headersData}
        data={recentOrders}
        handleCancelTrip={handleCancelTrip}
      />
    </div>
  );

  return (
    <>
      <DynamicMetaTags
        pageTitle="Beep - In-Depth Outlet Insights & Data"
        pageDescription="Take control of your outlets with Beep! Effortlessly manage new outlet requests, view detailed outlet information, track payment history, and set pickup prices. Enable or disable outlets with a click!"
        pageKeywords="Beep, outlet management, new outlet requests, outlet details, payment history, set pickup prices, enable outlets, disable outlets, active outlets"
      />
      <MobileResponsive />
      <div className="lg:flex hidden items-start">
        <Sidebar />

        {Object.values(sidebars).some((isOpen) => isOpen) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-30 transition-opacity duration-500 ease-in-out"></div>
        )}

        <div className="ml-[14.5vw] w-full h-screen overflow-y-auto px-10 py-8 text-secondary relative">
          <div className="flex w-full">
            <Link to="/outlets" className="flex items-center gap-3">
              <div className="bg-[#F75E00] w-5 h-5 flex items-center justify-center rounded-full pr-[1.5px]">
                <img
                  src="/assets/left-white-arrow.svg"
                  alt=""
                  className="w-[6px]"
                />
              </div>
              <span className="font-semibold text-base">Outlet details</span>
            </Link>
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center gap-2 w-full h-[75dvh]">
              <div className="h-12 w-12 rounded-full border-4 border-t-quaternary border-b-quaternary border-r-transparent border-l-transparent animate-spin"></div>
              <p className="text-gray-600 font-medium">Loading..</p>
            </div>
          ) : (
            <div className="w-full flex items-start">
              <div className="w-[41%]">
                <div className="py-8 w-full">
                  <div className="border-2 border-quaternary w-[84.5%] px-5 py-1.5 rounded-lg flex items-center gap-5 bg-[#F7F9F8]">
                    {/* <img
                      src="/assets/user.png"
                      alt={data?.outlet?.restaurant_name}
                      className="h-[5.3rem] w-[5.3rem] rounded-full"
                    /> */}

                    <Avatar
                      name={data?.outlet?.restaurant_name}
                      size="84"
                      round={true}
                      maxInitials={2}
                      textSizeRatio={2.5}
                      style={{ letterSpacing: "1px" }}
                    />
                    <div className="flex flex-col gap-1">
                      <h1 className="2xl:text-[21px] xl:text-[19px] lg:text-[19px] font-bold tracking-wide capitalize">
                        {data?.outlet?.restaurant_name}
                      </h1>
                      <p
                        className={`text-[13px] ${
                          data?.outlet?.is_verified
                            ? data?.outlet?.is_active
                              ? "text-[#25AD34]"
                              : "text-[#F34B50]"
                            : "text-[#F34B50]"
                        } font-semibold tracking-wide flex items-center gap-0.5 h-3 py-2 2xl:mb-2.5 xl:mb-1.5 lg:mb-1`}
                      >
                        <span className="2xl:pt-[3px] xl:pt-[1.5px] lg:pt-[1px]">
                          {data?.outlet?.is_verified
                            ? data?.outlet?.is_active
                              ? "Active"
                              : "Not Active"
                            : "Not Verified"}
                        </span>
                        <span className="2xl:text-3xl xl:text-[21px] lg:text-[23px]">
                          •
                        </span>
                      </p>
                    </div>
                  </div>

                  <div className="w-[84.5%] flex justify-center items-center gap-3 py-5 pt-6">
                    <div className="m-auto flex justify-center items-center gap-5 w-full">
                      <TernaryButton
                        text="Payment history"
                        path={`/outlets/weekly-earnings/${id}`}
                        uppercase={false}
                        mutation={true}
                        width="w-full"
                      />
                      <TernaryButton
                        text="Set pickup price"
                        uppercase={false}
                        mutation={true}
                        width="w-full"
                        event={() => toggleSidebar("orderPricing", true)}
                      />
                    </div>
                  </div>

                  <div className="w-[84.5%] mb-2 flex flex-col gap-2">
                    {/* API Key row */}
                    <button
                      onClick={() => {
                        setUrbanPiperKey("");
                        toggleSidebar("urbanPiper", true);
                      }}
                      className="w-full flex items-center justify-between px-5 py-2.5 rounded-lg border-2 border-quaternary bg-[#F7F9F8] hover:bg-orange-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-gray-700">Urbanpiper API Key</span>
                        {data?.outlet?.urban_piper_api_key ? (
                          <span className="flex items-center gap-1 text-xs font-semibold text-green-600 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                            Key added
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs font-semibold text-orange-600 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded-full">
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-400 inline-block" />
                            Not set
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-quaternary font-semibold">
                        {data?.outlet?.urban_piper_api_key ? "Update key →" : "Add key →"}
                      </span>
                    </button>

                    {/* Setup status row */}
                    <div className="w-full flex items-center justify-between px-5 py-2.5 rounded-lg border-2 border-quaternary bg-[#F7F9F8]">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-gray-700">Urbanpiper Setup</span>
                        {data?.outlet?.urban_piper_setup ? (
                          <span className="flex items-center gap-1 text-xs font-semibold text-green-600 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                            Setup complete
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs font-semibold text-orange-600 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded-full">
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-400 inline-block" />
                            Not set up
                          </span>
                        )}
                      </div>
                      {data?.outlet?.urban_piper_api_key && !data?.outlet?.urban_piper_setup && (
                        <button
                          onClick={handleSetupUrbanPiper}
                          disabled={urbanPiperSetupLoading}
                          className="text-xs font-semibold bg-quaternary text-white px-3 py-1 rounded-full hover:opacity-90 transition-opacity disabled:opacity-60"
                        >
                          {urbanPiperSetupLoading ? "Setting up…" : "Setup now →"}
                        </button>
                      )}
                    </div>
                  </div>

                  <h2 className="text-[17px] font-semibold pb-2">
                    Outlet details
                  </h2>

                  <div className="border-2 border-quaternary w-[84.5%] px-5 py-1.5 rounded-lg flex flex-col items-center justify-between bg-[#F7F9F8]">
                    {[
                      { label: "Outlet ID", value: data?.outlet?._id },
                      {
                        label: "Contact number",
                        value: data?.outlet?.phone_no,
                      },
                      {
                        label: "Email",
                        value: data?.outlet?.email || "N/A",
                      },
                      {
                        label: "Zipcode",
                        value: data?.outlet?.zip_code || "N/A",
                      },
                      {
                        label: "Address",
                        value: data?.outlet?.address || "N/A",
                      },
                      {
                        label: "Payout due",
                        value:
                          (data?.outlet?.outlet_payout_due &&
                            `£${data?.outlet?.outlet_payout_due}`) ||
                          "£0",
                      },
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start w-full py-2">
                        <p className="2xl:text-[15px] xl:text-[13px] lg:text-[13px] font-semibold tracking-wide w-1/2 break-all">
                          {item.label}
                        </p>
                        <p className="2xl:text-[15px] xl:text-[13px] lg:text-[13px] font-semibold tracking-wide w-1/2 break-all">
                          {item.value}
                        </p>
                      </div>
                    ))}
                  </div>

                  <h2 className="text-[17px] font-semibold pt-3 pb-2">
                    Outlet location
                  </h2>

                  <div className="border-2 border-quaternary w-[84.5%] h-52 rounded-lg flex flex-col justify-center items-center bg-[#F7F9F8] overflow-hidden">
                    {isLoading ? (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-4">
                        <div className="w-full h-full bg-gray-200 rounded-lg animate-pulse"></div>
                      </div>
                    ) : (
                      <Map
                        mapId={"terrain"}
                        defaultZoom={8}
                        defaultCenter={getDefaultCenter(
                          [data?.outlet],
                          "outlets"
                        )}
                        gestureHandling={"greedy"}
                        onClick={onMapClick}
                        disableDefaultUI
                        styles={"terrain"}
                        fullscreenControl={true}
                        mapTypeId={"terrain"}
                        mapTypeControlOptions
                        zoomControl
                        scaleControl
                        mapTypeControl
                        rotateControl
                      >
                        <MapMarker
                          data={[data?.outlet]}
                          entityType="outlets"
                          onMouseEnter={handleMarkerClick}
                        />

                        <InfoWindowPopup
                          infoWindowShown={infoWindowShown}
                          selectedMarker={selectedMarker}
                          handleCloseInfoWindow={handleCloseInfoWindow}
                        />
                      </Map>
                    )}
                  </div>
                </div>
              </div>

              <div className="w-[57%]">
                <div className="py-5 w-full">
                  <div className="flex justify-between items-center mb-2">
                    <h1 className="text-[21px] font-bold tracking-wide">
                      Order History
                    </h1>
                    <Link
                      to={`/history/outlets/${id}`}
                      className="text-[#F87638] text-[15px] font-bold tracking-wide flex items-center gap-1.5"
                    >
                      View more
                      <img
                        src="/assets/right-arrow-orange.svg"
                        alt=""
                        className="h-[11px] mt-0.5"
                      />
                    </Link>
                  </div>
                  <div className="w-full rounded-lg flex flex-col items-center justify-between gap-4">
                    {renderTable()}
                  </div>

                  <div className="w-full py-5">
                    <div className="m-auto flex items-center gap-5 w-[60%]">
                      <TernaryButton
                        text={
                          data?.outlet?.is_suspended
                            ? "Enable outlet"
                            : "Suspend outlet"
                        }
                        uppercase={false}
                        mutation={true}
                        width="w-full"
                        event={() => toggleSidebar("suspendOutlet", true)}
                      />
                      <TernaryButton
                        text="Delete outlet"
                        uppercase={false}
                        mutation={true}
                        width="w-full"
                        event={() => toggleSidebar("deleteOutlet", true)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Suspend Outlet Sidebar */}
          <div
            ref={sidebarRefs.suspendOutlet}
            className={`fixed right-0 top-0 h-full w-[40%] bg-white shadow-lg overflow-y-auto transform transition-transform duration-[500ms] ease-in-out z-40 border-t-4 border-primary ${
              sidebars.suspendOutlet ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="p-8 relative flex flex-col items-center">
              <button
                onClick={handleCloseSidebar}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 scale-150"
              >
                &times;
              </button>

              <div className="flex w-[90%] pb-10">
                <div className="flex items-center gap-3 cursor-pointer">
                  <div className="bg-[#F75E00] w-6 h-6 flex items-center justify-center rounded-full">
                    <img
                      src="/assets/left-white-arrow.svg"
                      alt=""
                      className="w-[7px]"
                    />
                  </div>
                  <span className="font-semibold text-[25px]">
                    {!data?.outlet?.is_suspended
                      ? "Suspend outlet"
                      : "Enable outlet"}
                  </span>
                </div>
              </div>

              <div className="border-2 border-quaternary w-[90%] mb-5 px-5 py-1.5 rounded-lg flex items-center gap-3 bg-[#F7F9F8]">
                {/* <img
                  src="/assets/user.png"
                  alt={data?.outlet?.restaurant_name}
                  className="h-[5.3rem] w-h-[5.3rem] rounded-full"
                /> */}
                <Avatar
                  name={data?.outlet?.restaurant_name}
                  size="84"
                  round={true}
                  maxInitials={2}
                  textSizeRatio={2.5}
                  style={{ letterSpacing: "1px" }}
                />
                <div className="text-center">
                  <h1 className="text-xl font-bold tracking-wide capitalize">
                    {data?.outlet?.restaurant_name}
                  </h1>
                </div>
              </div>

              <div className="w-[90%] mb-5 px-5 py-3 rounded-lg flex justify-between items-center shadow-lg">
                <p className="text-[18px] font-semibold w-full">
                  {data?.outlet?.phone_no}
                </p>
                <div className="w-full flex justify-end">
                  <PrimaryButton
                    text={"Call outlet"}
                    path={`tel:${data?.outlet?.phone_no}`}
                    width="w-[65%]"
                    uppercase={false}
                  />
                </div>
              </div>

              <div className="w-[90%] px-[4px] py-5 rounded-lg">
                <p className="text-lg">
                  {!suspendOutlet.suspendOutlet &&
                  !data?.outlet?.is_suspended ? (
                    <>
                      The outlet{" "}
                      <strong> {data?.outlet?.restaurant_name}</strong> will not
                      be able to accept orders and request deliveries but will
                      be able to access their account.
                      {/* This account will be suspended for
                      <strong> one week</strong> due to ongoing order dispatch
                      problems and multiple driver reports. We value your
                      cooperation in restoring operational efficiency. */}
                    </>
                  ) : !suspendOutlet.suspendOutletConfirm &&
                    !data?.outlet?.is_suspended ? (
                    <>
                      This account will be suspended until you{" "}
                      <strong>reactivate</strong> it. Are you sure you want to
                      suspend {data?.outlet?.restaurant_name}'s account?
                    </>
                  ) : (
                    <>
                      This account is suspended successfully. Are you sure you
                      want to <strong> enable</strong>{" "}
                      {data?.outlet.restaurant_name}'s account?
                    </>
                  )}
                </p>
              </div>

              <div className="w-[90%] m-auto flex flex-col justify-center gap-5 py-5">
                <PrimaryButton
                  text={
                    !suspendOutlet.suspendOutlet && !data?.outlet?.is_suspended
                      ? "Suspend outlet"
                      : !suspendOutlet.suspendOutletConfirm &&
                        !data?.outlet?.is_suspended
                      ? "Suspend outlet"
                      : "Enable"
                  }
                  width="w-full"
                  uppercase={false}
                  event={
                    !data?.outlet?.is_suspended
                      ? suspendOutlet.suspendOutlet
                        ? suspendOutlet.suspendOutletConfirm
                          ? suspendOutlet.enableSuspendedOutlet
                            ? null
                            : handleEnableSuspendedOutlet
                          : handleSuspendOutletConfirm
                        : handleSuspendOutlet
                      : handleEnableSuspendedOutlet
                  }
                />
              </div>
            </div>
          </div>

          {/* Cancel Trip Sidebar */}
          <div
            ref={sidebarRefs.cancelTrip}
            className={`fixed right-0 top-0 h-full w-[40%] bg-white shadow-lg overflow-y-auto transform transition-transform duration-[500ms] ease-in-out z-40 border-t-4 border-primary ${
              sidebars.cancelTrip ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="p-8 relative flex flex-col items-center">
              <button
                onClick={handleCloseSidebar}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 scale-150"
              >
                &times;
              </button>
              <div className="flex w-[90%] pb-10">
                <div className="flex items-center gap-3 cursor-pointer">
                  <span className="font-semibold text-[25px]">Cancel trip</span>
                </div>
              </div>
              <div className="border-2 border-quaternary w-[90%] mb-5 px-5 py-1.5 rounded-lg flex items-center gap-3 bg-[#F7F9F8]">
                {/* <img
                  src="/assets/user.png"
                  alt={data?.outlet?.restaurant_name}
                  className="h-[5.3rem] w-h-[5.3rem] rounded-full"
                /> */}
                <Avatar
                  name={data?.outlet?.restaurant_name}
                  size="84"
                  round={true}
                  maxInitials={2}
                  textSizeRatio={2.5}
                  style={{ letterSpacing: "1px" }}
                />
                <div className="text-center">
                  <h1 className="text-xl font-bold tracking-wide capitalize">
                    {data?.outlet?.restaurant_name}
                  </h1>
                </div>
              </div>
              <div className="w-[90%] mb-5 px-5 py-3 rounded-lg flex justify-between items-center shadow-lg">
                <p className="text-[18px] font-semibold w-full">
                  {data?.outlet?.phone_no}
                </p>
                <div className="w-full flex justify-end">
                  <PrimaryButton
                    text={"Call outlet"}
                    path={`tel:${data?.outlet?.phone_no}`}
                    width="w-[65%]"
                    uppercase={false}
                  />
                </div>
              </div>
              <div className="border-2 border-quaternary w-[90%] mb-5 p-[4px] rounded-lg flex flex-col items-center bg-[#F7F9F8]">
                <h1 className="bg-primary w-full text-sm py-[7px] font-bold tracking-wide rounded-md text-center text-white uppercase">
                  Order details
                </h1>
                <div className="w-[94.5%] px-5 py-4 rounded-lg flex flex-col items-center justify-between gap-4 bg-[#F7F9F8]">
                  {[
                    { label: "Order ID", value: cancelTripData?.orderId },
                    { label: "Driver's ID", value: cancelTripData?.driver?.id },
                    {
                      label: "Driver's contact no.",
                      value: cancelTripData?.driver?.phone,
                    },
                    {
                      label: "Customer's name",
                      value: cancelTripData?.customer_name,
                    },
                    {
                      label: "Customer's contact no.",
                      value: cancelTripData?.customer_phone,
                    },
                    {
                      label: "Delivery price",
                      value: cancelTripData?.delivery_price
                        ? `£${cancelTripData.delivery_price}`
                        : "£0",
                    },
                    { label: "Zipcode", value: cancelTripData?.zipcode },
                    {
                      label: "Mode of Payment",
                      value: cancelTripData?.mode_of_payment,
                    },
                    {
                      label: "Delivery Items",
                      value: Array.isArray(cancelTripData?.delivery_items)
                        ? cancelTripData.delivery_items
                            .map((item) => `${item.item} (${item.quantity})`)
                            .join(", ")
                        : typeof cancelTripData?.delivery_items === "object" &&
                          cancelTripData?.delivery_items !== null
                        ? "Object data (cannot display directly)"
                        : cancelTripData?.delivery_items || "—",
                    },
                    {
                      label: "Food Order Value",
                      value: cancelTripData?.food_order_value
                        ? `£${cancelTripData.food_order_value}`
                        : "£0",
                    },
                    { label: "Address", value: cancelTripData?.address },
                  ].map((detail, index) => (
                    <div className="flex items-center w-full" key={index}>
                      <p className="2xl:text-[15px] xl:text-[13px] lg:text-[13px] font-semibold tracking-wide 2xl:w-[65%] xl:w-[55%] lg:w-[55%]">
                        {detail.label}
                      </p>
                      <p className="2xl:text-[15px] xl:text-[13px] lg:text-[13px] font-semibold tracking-wide text-end w-full">
                        {detail.value || "—"}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="w-[90%] m-auto flex flex-col justify-center gap-5 py-5">
                {cancelTrip ? (
                  <PrimaryButton
                    text={"Allocate new driver"}
                    width="w-full"
                    uppercase={false}
                    path={`/allocate-new-driver/${cancelTripData?.orderId}`}
                  />
                ) : (
                  <PrimaryButton
                    text={"Cancel trip"}
                    width="w-full"
                    uppercase={false}
                    event={handleCancelTripConfirm}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Set Pickup Price Sidebar */}
          <div
            ref={sidebarRefs.orderPricing}
            className={`fixed right-0 top-0 h-full w-[40%] bg-white shadow-lg overflow-y-auto transform transition-transform duration-[500ms] ease-in-out z-40 border-t-4 border-primary ${
              sidebars.orderPricing ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="p-8 relative flex flex-col items-center">
              <button
                onClick={handleCloseSidebar}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 scale-150"
              >
                &times;
              </button>
              <div className="flex w-[90%] pb-10">
                <span className="font-semibold text-[25px]">
                  Set Order Pickup Price
                </span>
              </div>
              <div className="border-2 border-quaternary w-[90%] mb-5 px-5 py-1.5 rounded-lg flex items-center gap-3 bg-[#F7F9F8]">
                {/* <img
                  src="/assets/user.png"
                  alt={data?.outlet?.restaurant_name}
                  className="h-[5.3rem] w-h-[5.3rem] rounded-full"
                /> */}
                <Avatar
                  name={data?.outlet?.restaurant_name}
                  size="84"
                  round={true}
                  maxInitials={2}
                  textSizeRatio={2.5}
                  style={{ letterSpacing: "1px" }}
                />
                <div className="text-center">
                  <h1 className="text-xl font-bold tracking-wide capitalize">
                    {data?.outlet?.restaurant_name}
                  </h1>
                </div>
              </div>
              <div className="w-[90%] mb-5 px-5 py-3 rounded-lg flex justify-between items-center shadow-lg">
                <p className="text-[18px] font-semibold w-full">
                  {data?.outlet?.phone_no}
                </p>
                <div className="w-full flex justify-end">
                  <PrimaryButton
                    text={"Call outlet"}
                    path={`tel:${data?.outlet?.phone_no}`}
                    width="w-[65%]"
                    uppercase={false}
                  />
                </div>
              </div>

              <form action="" className="w-[90%]" onSubmit={handleOrderPricing}>
                <div className="py-5 flex flex-col gap-5">
                  <CustomInput
                    label="Set pricing for 1 order"
                    value={pricing.priceFor1.value}
                    onChange={(e) => handlePriceChange(e, "priceFor1")}
                    isDisabled={pricing.priceFor1.disabled}
                    toggleEdit={() => togglePriceEdit("priceFor1")}
                    validationError={validationErrors.priceFor1}
                  />
                  <CustomInput
                    label="Set pricing for 2 orders"
                    value={pricing.priceFor2.value}
                    onChange={(e) => handlePriceChange(e, "priceFor2")}
                    isDisabled={pricing.priceFor2.disabled}
                    toggleEdit={() => togglePriceEdit("priceFor2")}
                    validationError={validationErrors.priceFor2}
                  />
                  <CustomInput
                    label="Set pricing for 3 orders"
                    value={pricing.priceFor3.value}
                    onChange={(e) => handlePriceChange(e, "priceFor3")}
                    isDisabled={pricing.priceFor3.disabled}
                    toggleEdit={() => togglePriceEdit("priceFor3")}
                    validationError={validationErrors.priceFor3}
                  />
                </div>

                <div className="w-full m-auto flex flex-col justify-center gap-5 pt-5">
                  <PrimaryButton
                    text="Confirm"
                    width="w-full"
                    type="submit"
                    uppercase={false}
                  />
                </div>
              </form>
            </div>
          </div>

          {/* Delete Outlet Sidebar */}
          <div
            ref={sidebarRefs.deleteOutlet}
            className={`fixed right-0 top-0 h-full w-[40%] bg-white shadow-lg overflow-y-auto transform transition-transform duration-[500ms] ease-in-out z-40 border-t-4 border-primary ${
              sidebars.deleteOutlet ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="p-8 relative flex flex-col items-center">
              <button
                onClick={handleCloseSidebar}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 scale-150"
              >
                &times;
              </button>
              <div className="flex w-[90%] pb-10">
                <div className="flex items-center gap-3 cursor-pointer">
                  <div className="bg-[#F75E00] w-6 h-6 flex items-center justify-center rounded-full">
                    <img
                      src="/assets/left-white-arrow.svg"
                      alt=""
                      className="w-[7px]"
                    />
                  </div>
                  <span className="font-semibold text-[25px]">
                    Delete outlet
                  </span>
                </div>
              </div>

              <div className="border-2 border-quaternary w-[90%] mb-5 px-5 py-1.5 rounded-lg flex items-center gap-3 bg-[#F7F9F8]">
                {/* <img
                  src="/assets/user.png"
                  alt={data?.outlet?.restaurant_name}
                  className="h-[5.3rem] w-h-[5.3rem] rounded-full"
                /> */}
                <Avatar
                  name={data?.outlet?.restaurant_name}
                  size="84"
                  round={true}
                  maxInitials={2}
                  textSizeRatio={2.5}
                  style={{ letterSpacing: "1px" }}
                />
                <div className="text-center">
                  <h1 className="text-xl font-bold tracking-wide capitalize">
                    {data?.outlet?.restaurant_name}
                  </h1>
                </div>
              </div>

              <div className="w-[90%] mb-5 px-5 py-3 rounded-lg flex justify-between items-center shadow-lg">
                <p className="2xl:text-[18px] xl:text-[18px] lg:text-[16px] font-semibold w-full">
                  {data?.outlet?.phone_no}
                </p>
                <div className="w-full flex justify-end items-center">
                  <PrimaryButton
                    text={"Call outlet"}
                    path={`tel:${data?.outlet?.phone_no}`}
                    width="w-[60%]"
                    uppercase={false}
                  />
                </div>
              </div>

              <div className="w-[90%] px-[4px] py-5 rounded-lg">
                <div className="2xl:text-lg xl:text-lg lg:text-[16px]">
                  {!deleteOutlet.deleteOutlet ? (
                    <>
                      <p>
                        The outlet{" "}
                        <strong className="capitalize">
                          {data?.outlet?.restaurant_name}
                        </strong>{" "}
                        will be permanently deleted from the database and will
                        loose access to their account.
                        {/* This account is scheduled for
                        permanent deletion. If you wish to proceed with deleting
                        the account{" "}
                        <strong>{data?.outlet?.restaurant_name}</strong>, please
                        request a one-time password (OTP) for confirmation. */}
                      </p>

                      <div className="w-[90%] m-auto flex flex-col justify-center gap-5 py-10">
                        <PrimaryButton
                          text="Request a OTP"
                          width="w-full"
                          uppercase={false}
                          event={handleSendOtp}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <form
                        action=""
                        className="w-full"
                        onSubmit={(e) => e.preventDefault()}
                      >
                        <div className="py-5 flex flex-col gap-5">
                          <label
                            htmlFor="OTP"
                            className="2xl:text-[17px] xl:text-[15px] lg:text-[14px] font-semibold text-[#413F3F]"
                          >
                            Please enter the OTP sent to your email address
                          </label>
                          <FloatingInput
                            name="otp"
                            id="otp"
                            type="number"
                            placeholder="Enter OTP"
                            value={otp}
                            error={errors.otp}
                            handleBlur={handleOtpBlur}
                            onChange={(e) => setOtp(e.target.value)}
                          />
                        </div>

                        <div className="w-full m-auto flex flex-col justify-center gap-5 pt-5">
                          <PrimaryButton
                            text="Confirm"
                            width="w-full"
                            uppercase={false}
                            event={handleDeleteOutlet}
                          />
                        </div>
                      </form>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* Urbanpiper Setup Modal */}
          {showSetupModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center">
              <div className="bg-white rounded-2xl shadow-2xl w-[90%] max-w-md p-8 flex flex-col items-center gap-5 border-t-4 border-quaternary">
                <div className="w-16 h-16 rounded-full bg-orange-50 border-2 border-quaternary flex items-center justify-center">
                  <svg className="w-8 h-8 text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-800 text-center">Urbanpiper Setup Initiated</h2>
                <p className="text-sm text-gray-600 text-center leading-relaxed">
                  The Urbanpiper integration for{" "}
                  <strong className="capitalize">{data?.outlet?.restaurant_name}</strong> is being set up.
                  <br /><br />
                  This process typically takes <strong>10–15 minutes</strong>. You can come back later to check the setup status.
                </p>
                <button
                  onClick={() => setShowSetupModal(false)}
                  className="w-full bg-quaternary text-white font-semibold py-2.5 rounded-lg hover:opacity-90 transition-opacity"
                >
                  Got it
                </button>
              </div>
            </div>
          )}

          {/* Urbanpiper API Key Sidebar */}
          <div
            ref={urbanPiperRef}
            className={`fixed right-0 top-0 h-full w-[40%] bg-white shadow-lg overflow-y-auto transform transition-transform duration-[500ms] ease-in-out z-40 border-t-4 border-primary ${
              sidebars.urbanPiper ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="p-8 relative flex flex-col items-center">
              <button
                onClick={handleCloseSidebar}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 scale-150"
              >
                &times;
              </button>
              <div className="flex w-[90%] pb-10">
                <span className="font-semibold text-[25px]">Urbanpiper API Key</span>
              </div>

              <div className="border-2 border-quaternary w-[90%] mb-5 px-5 py-1.5 rounded-lg flex items-center gap-3 bg-[#F7F9F8]">
                <Avatar
                  name={data?.outlet?.restaurant_name}
                  size="84"
                  round={true}
                  maxInitials={2}
                  textSizeRatio={2.5}
                  style={{ letterSpacing: "1px" }}
                />
                <div>
                  <h1 className="text-xl font-bold tracking-wide capitalize">
                    {data?.outlet?.restaurant_name}
                  </h1>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {data?.outlet?.urban_piper_api_key ? (
                      <span className="flex items-center gap-1 text-green-600 font-semibold">
                        <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                        API key already set
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-orange-500 font-semibold">
                        <span className="w-2 h-2 rounded-full bg-orange-400 inline-block" />
                        No API key set
                      </span>
                    )}
                  </p>
                </div>
              </div>

              <form className="w-[90%]" onSubmit={handleSaveUrbanPiperKey}>
                <div className="py-5 flex flex-col gap-3">
                  <label className="text-sm font-semibold text-gray-700">
                    {data?.outlet?.urban_piper_api_key ? "Enter new API key to update" : "Enter Urbanpiper API key"}
                  </label>
                  <input
                    type="text"
                    value={urbanPiperKey}
                    onChange={(e) => setUrbanPiperKey(e.target.value)}
                    placeholder="up_api_xxxxxxxxxxxxxxxx"
                    className="w-full border-2 border-gray-200 focus:border-quaternary rounded-lg px-4 py-2.5 text-sm font-mono outline-none transition-colors"
                  />
                  <p className="text-xs text-gray-400">The key will be encrypted before storage.</p>
                </div>

                <div className="w-full flex flex-col gap-3 pt-2">
                  <PrimaryButton
                    text={urbanPiperSaving ? "Saving…" : (data?.outlet?.urban_piper_api_key ? "Update key" : "Save key")}
                    width="w-full"
                    type="submit"
                    uppercase={false}
                    disabled={urbanPiperSaving}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OutletDetail;
